// Tenant brand-kit -> jrni-tokens CSS-variable bridge.
//
// jrni-tokens/jrni-ui are CD-owned and byte-locked (never edited here). But
// every jrni-ui primitive (Button, Input, Card, Dropdown, Menu, IconRail,
// ...) reads its colors through `var(--jrni-color-*, <default-hex>)` CSS
// custom properties resolved at `:root` (see packages/jrni-tokens/src/styles/
// tokens.css + packages/shell/src/ThemeProvider.tsx). This module is a
// *runtime* mechanism, entirely inside the platform harness: it fetches the
// tenant's live brand kit (GET /v1/branding/theme, already-derived +
// WCAG-checked by workers/events/src/canvas-brand-derivation.ts) and sets
// those same custom properties as INLINE styles on <html>. An inline style
// on the root element has higher cascade specificity than any selector-based
// rule (:root {...}, [data-tenant="cns"] {...}), so this cleanly overrides
// the JRNI defaults for every jrni-ui component -- without touching a single
// locked byte in jrni-tokens or jrni-ui.
//
// Absent/failed brand-kit fetch -> no overrides are set -> JRNI defaults
// stand untouched. Fully tenant-agnostic: nothing here names a tenant.
//
// KNOWN LIMIT (documented, not silently swallowed): jrni-ui components that
// consume `var(--jrni-color-*)` pick this up automatically. Claude Design's
// own screen-level styling in `cd-app/screens/**` frequently hardcodes the
// JRNI purple as a literal hex in an inline `style` prop instead of reading
// the CSS variable -- those specific hardcoded elements are NOT reachable by
// this bridge (see docket in the PR description / commit body for the exact
// count). That gap is a CD-authored-styling issue, tracked as a follow-up
// ask to CD, not something this runtime bridge can fix without touching
// CD's screens.
import { fetchTenantTheme } from "@v0y4ge/api-client";

/** Point the tab icon at `url` (2026-10-04). Same rule as cd-app's applyTenantFavicon: only an https URL or a
 *  same-origin path, so an empty, http or malformed value leaves the bundled icon. Kept here rather than
 *  imported: this bridge runs at the boot of every public entry, and branded-chrome pulls Studio's session
 *  module (shell/access), which reads `window` when it loads. */
export function applyBootFavicon(url: string | null | undefined): void {
  if (typeof document === "undefined" || typeof window === "undefined" || !url) return;
  let ok = false;
  try { const u = new URL(url, window.location.href); ok = u.protocol === "https:" || u.origin === window.location.origin; } catch { ok = false; }
  if (!ok) return;
  let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
  link.removeAttribute("type");
  link.href = url;
}

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return null;
  const int = Number.parseInt(m[1]!, 16);
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

/** Mix a hex color toward white by `t` (0..1) -- generic tint-ladder helper.
 *  Tenant-agnostic: works for any brand hex, not tuned per tenant. */
function tint(hex: string, t: number): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  const mix = (c: number) => Math.round(c + (255 - c) * t);
  return `#${[mix(r), mix(g), mix(b)].map((c) => c.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** WCAG relative luminance of `hex`, or null for a colour it cannot read. */
function relativeLuminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

/** WCAG contrast between two colours, or 0 when either cannot be read. Kept
 *  local rather than imported: this bridge runs at the boot of every public
 *  entry, and the shared helpers live in larger modules (see applyBootFavicon). */
function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return 0;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** WCAG contrast of white text on `hex`, or 0 for a colour it cannot read. */
function contrastWithWhite(hex: string): number {
  return contrastRatio("#FFFFFF", hex);
}

// The primary ramp's tints, in hundredths so the search below steps exactly.
const P2_TINT = 60;
const P3_TINT = 78;
const P4_TINT = 90;
/** Highest tint p3 may take: leaves room for p4 above it, below pure white. */
const P3_TINT_MAX = 96;
/** p4 stays at least this many hundredths lighter than p3. */
const P4_MIN_STEP = 2;

/**
 * The p3 tint (primary-3 / brand-primary-muted / fill-primary-2). Selected
 * chips, tabs and radios draw the primary text colour on this fill, so it is
 * the smallest tint from 0.78 upward at which `text` reads at WCAG AA (4.5:1)
 * on it. A brand that already passes at 0.78 keeps exactly that fill.
 *
 * KNOWN LIMIT: a text colour that cannot reach 4.5:1 on any tint up to
 * P3_TINT_MAX (a pale yellow brand with no darker link is ~1.45:1 even on
 * white) keeps the 0.78 fill. Washing the fill towards white would buy almost
 * no contrast and would lose the selected-state cue. The text colour itself is
 * out of scope here: the server's derived link is the fix for that case.
 */
function readableP3Tint(primary: string, text: string): number {
  if (relativeLuminance(text) === null) return P3_TINT;
  for (let k = P3_TINT; k <= P3_TINT_MAX; k += 1) {
    const fill = tint(primary, k / 100);
    if (!fill) return P3_TINT;
    if (contrastRatio(text, fill) >= 4.5) return k;
  }
  return P3_TINT;
}

function rgba(hex: string, alpha: number): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const SYSTEM_SAFE_FONTS = new Set(["Helvetica Neue", "Arial", "Georgia"]);
const _loadedFonts = new Set<string>();

/** Load a Google Fonts stylesheet for a brand-kit heading/body font. No-op
 *  for system-safe fallbacks (no network fetch needed) and repeat calls.
 *  Mirrors the pattern already used for public landing pages
 *  (cd-app/screens/hubs/PublicHubs.tsx ensureLandingFont). */
function ensureBrandFont(family: string | undefined | null): void {
  if (!family || SYSTEM_SAFE_FONTS.has(family) || _loadedFonts.has(family)) return;
  _loadedFonts.add(family);
  const id = `tenant-brand-font-${family.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function setVar(name: string, value: string | null | undefined): void {
  if (!value) return;
  document.documentElement.style.setProperty(name, value);
}

/** The design system's own value for a token, read from the live stylesheet
 *  so nothing here restates a jrni-tokens hex. An inline override already on
 *  <html> is lifted for the read: the bridge runs once per boot today, but a
 *  second run must not mistake its own earlier write for the default. */
function designSystemDefault(name: string): string {
  const root = document.documentElement;
  const inline = root.style.getPropertyValue(name);
  if (inline) root.style.removeProperty(name);
  const value = getComputedStyle(root).getPropertyValue(name).trim();
  if (inline) root.style.setProperty(name, inline);
  return value;
}

function sameColor(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export interface TenantThemeColors {
  brandColor: string;
  /** The accent the tenant stored. Null when it never chose one -- the
   *  server then substitutes a platform fallback, which is not the tenant's
   *  intent and must not displace the design system's own focus ring. */
  accentColor: string | null | undefined;
  coverColor: string;
  link?: string | null;
}

export interface DesignSystemDefaults {
  primary: string;
  coverBg: string;
}

/**
 * The CSS-variable overrides a tenant brand needs -- and ONLY those.
 *
 * The white-mix ladder below is a generic approximation for an arbitrary
 * brand hex. The design system's own ramp is hand-tuned, so a tenant whose
 * colour IS the design-system default must get no override at all: mixing
 * the default toward white yields a visibly different hover/tint ramp than
 * the one the design system ships, and the tenant then renders unlike the
 * design it was composed from. Each family is therefore skipped when its
 * source colour equals the design-system default.
 *
 * KNOWN LIMIT: `defaults` is read under the active theme. Under
 * `[data-theme="dark"]` the design system re-tunes primary-1, so a
 * default-brand tenant compares unequal there and is overridden as before.
 * Studio pins light today (cd-app/App.tsx), so this is latent.
 */
export function planTenantThemeVars(
  colors: TenantThemeColors,
  defaults: DesignSystemDefaults,
): Record<string, string> {
  const vars: Record<string, string> = {};
  const put = (name: string, value: string | null | undefined) => {
    if (value) vars[name] = value;
  };
  const primary = colors.brandColor;
  const accent = colors.accentColor;

  // --- Primary color family (brandColor -> --jrni-color-primary-*) ---
  if (!sameColor(primary, defaults.primary)) {
    const primaryText = colors.link ?? primary;
    const p3Tint = readableP3Tint(primary, primaryText);
    // p4 keeps its 0.90 tint unless p3 climbs past 0.88; then p4 stays 0.02
    // above it so the ramp stays ordered p2 < p3 < p4 in lightness.
    const p4Tint = Math.max(P4_TINT, p3Tint + P4_MIN_STEP);
    const p2 = tint(primary, P2_TINT / 100);
    const p3 = tint(primary, p3Tint / 100);
    const p4 = tint(primary, p4Tint / 100);
    put("--jrni-color-primary-1", primary);
    put("--jrni-color-primary-2", p2);
    put("--jrni-color-primary-3", p3);
    put("--jrni-color-primary-4", p4);

    put("--jrni-color-brand-primary", primary);
    put("--jrni-color-brand-primary-hover", p2);
    put("--jrni-color-brand-primary-muted", p3);

    put("--jrni-color-fill-primary-1", primary);
    put("--jrni-color-fill-primary-2", p3);
    put("--jrni-color-fill-primary-3", p4);

    // AA-checked darker variant already computed server-side (canvas-brand-
    // derivation's toneAdjustedForContrast) -- reuse it instead of guessing.
    put("--jrni-color-primary-text", primaryText);
    // Solid brand controls (primary button, checked checkbox, on toggle, active
    // pagination) fill with brand-fill and carry white ink. The design system's
    // light value is the raw brand (white on a pale brand is ~1.45:1) and its dark
    // value is the default brand's own fill, so a tenant would show the default
    // hue there. The AA-checked link tone reads at 4.5:1 against a light canvas,
    // so white ink on it reads at least as well; it is the raw brand when the
    // server derived no darker link.
    put("--jrni-color-brand-fill", primaryText);

    put("--jrni-color-brand-glow", rgba(primary, 0.30));
    put("--jrni-color-brand-glow-strong", rgba(primary, 0.35));
  }

  // --- Accent color (accentColor -> focus ring / cover accent) ---
  // Applied only for an accent the tenant really has: none stored, or one
  // equal to the design-system primary, is "no accent of its own". The design
  // system binds its focus ring to that primary for contrast and hand-picks
  // its cover accent, so both are left standing rather than approximated.
  if (accent && !sameColor(accent, defaults.primary)) {
    put("--jrni-color-utility-focus", tint(accent, 0.55));
    put("--jrni-color-cover-accent", tint(accent, 0.35));
  }

  // --- Cover / dark surfaces (coverColor -> PDF/onboarding splash bg, app chrome) ---
  if (!sameColor(colors.coverColor, defaults.coverBg)) {
    put("--jrni-color-cover-bg", colors.coverColor);
    // The app chrome (Studio's top bar) has the cover's contract: a fixed-dark surface under white
    // text in both themes. So a tenant's cover is its bar too, when white text on it is readable
    // (AA); a light cover leaves the design system's bar standing.
    if (colors.coverColor && contrastWithWhite(colors.coverColor.trim()) >= 4.5) {
      put("--jrni-color-chrome-nav", colors.coverColor);
    }
  }

  return vars;
}

/** Race a promise against a timeout so a slow/unreachable branding endpoint
 *  can never hang app boot -- resolves to null on timeout, letting JRNI
 *  defaults stand. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    p.then((v) => { clearTimeout(timer); resolve(v); })
      .catch(() => { clearTimeout(timer); resolve(null); });
  });
}

/**
 * Fetch the tenant's brand kit and override every jrni-tokens CSS variable
 * this runtime mechanism can reach. Fail-open: any error (network, parse,
 * missing branding row) leaves JRNI defaults in place -- this must never
 * block or break app boot.
 */
export async function applyTenantThemeBridge(): Promise<void> {
  // 4s: a Cloudflare edge/worker cold path (D1 read + first-hit warmup right
  // after a deploy) can occasionally exceed 1.5s -- verified live against
  // Live-tenant regression: the very first request after a fresh Pages deploy raced past a
  // 1.5s budget and left JRNI defaults standing, while every subsequent
  // (warm) request resolved in ~400ms. 4s covers the cold case without
  // meaningfully delaying first paint on the warm path that's the norm.
  const theme = await withTimeout(fetchTenantTheme(), 4000);
  if (!theme) return;

  const { brand, derived } = theme;
  const vars = planTenantThemeVars(
    {
      brandColor: brand.brandColor,
      accentColor: brand.accentColor,
      coverColor: brand.coverColor,
      link: derived.link,
    },
    {
      primary: designSystemDefault("--jrni-color-primary-1"),
      coverBg: designSystemDefault("--jrni-color-cover-bg"),
    },
  );
  for (const [name, value] of Object.entries(vars)) setVar(name, value);

  // The tab icon: the tenant's favicon, else its logo (2026-10-04). Studio never applied it, so every
  // tenant's Studio and sign-in tab showed the bundled icon. applyBootFavicon ignores an empty or
  // non-https URL, which keeps the bundled icon.
  applyBootFavicon(brand.faviconUrl || brand.logoUrl);

  // --- Fonts (headingFont/bodyFont -> the single --jrni-font-family-sans
  // token jrni-tokens exposes today; see docket -- there is currently no
  // separate heading-vs-body font token to bridge into). ---
  const bodyFont = brand.bodyFont;
  if (bodyFont) {
    ensureBrandFont(bodyFont);
    setVar(
      "--jrni-font-family-sans",
      `'${bodyFont}', 'Helvetica Neue', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif`,
    );
  }
  if (brand.headingFont && brand.headingFont !== bodyFont) {
    ensureBrandFont(brand.headingFont);
  }
}
